"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { twMerge } from "tailwind-merge";

export interface SelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export interface SelectInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: readonly SelectOption<string>[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  name?: string;
  "data-testid"?: string;
}

export function SelectInput({
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  disabled,
  error,
  className,
  name,
  "data-testid": dataTestId,
}: SelectInputProps) {
  return (
    <SelectPrimitive.Root
      value={value || undefined}
      onValueChange={onChange}
      disabled={disabled}
      name={name}
    >
      <SelectPrimitive.Trigger
        id={name}
        onBlur={onBlur}
        data-testid={dataTestId}
        className={twMerge(
          "flex w-full cursor-pointer items-center justify-between rounded-[12px] border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-[14px] font-medium text-[#1A1F2E] transition-colors duration-150 outline-none",
          "data-placeholder:text-[#11182780]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-[#E02800] focus:border-[#E02800]",
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className="size-4 shrink-0 text-[#6B7280] transition-transform duration-200 in-data-[state=open]:rotate-180"
            strokeWidth={2}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={twMerge(
            "z-50 w-(--radix-select-trigger-width) overflow-hidden rounded-[12px] border-1 border-[#E5E8ED] bg-white py-1.5",
            "shadow-[0_4px_24px_rgba(0,0,0,0.10),0_0_0_1px_rgba(0,0,0,0.05)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className={twMerge(
                  "relative mx-1 flex cursor-pointer items-center justify-between rounded-[8px] px-3 py-3 text-[14px] font-medium text-[#1A1F2E] outline-none select-none",
                  "data-highlighted:bg-[#EEF4FF]",
                  "data-[state=checked]:bg-[#EEF4FF]",
                )}
              >
                <SelectPrimitive.ItemText>
                  {option.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <Image
                    src="/icons/hand-drown-check.svg"
                    alt=""
                    width={12}
                    height={23}
                  />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
